export const COMMON_EMAIL_DOMAINS: string[] = [
    // Google
    'gmail.com',
    'googlemail.com',

    // Microsoft
    'outlook.com',
    'outlook.co.uk',
    'hotmail.com',
    'hotmail.co.uk',
    'live.com',
    'live.co.uk',
    'msn.com',

    // Yahoo
    'yahoo.com',
    'yahoo.co.uk',
    'ymail.com',
    'rocketmail.com',

    // Apple
    'icloud.com',
    'me.com',
    'mac.com',

    // GMX / WEB.DE (DE)
    'gmx.com',
    'gmx.de',
    'web.de',

    // Proton
    'proton.me',
    'protonmail.com',

    // Other free providers
    'mail.com',
    'zoho.com',
    'aol.com',

    // UK-providers
    'btinternet.com',
    'sky.com',
    'virginmedia.com',
];

export const ROLE_EMAIL_PREFIXES: string[] = [
    'admin',
    'support',
    'info',
    'sales',
    'contact',
    'help',
    'billing',
    'webmaster',
    'postmaster',
    'abuse',
    'noreply',
];

export const DISPOSABLE_DOMAINS: string[] = [
    // 10-minute / temp mail
    '10minutemail.com',
    '10minutemail.net',
    'tempmail.com',
    'tempmail.net',
    'temp-mail.org',
    'tempmail.email',

    // Mailinator
    'mailinator.com',
    'mailinator.org',

    // GuerrillaMail
    'guerrillamail.com',
    'guerrillamail.info',
    'guerrillamail.biz',
    'guerrillamail.de',
    'sharklasers.com',

    // YOPmail
    'yopmail.com',
    'yopmail.net',

    // TrashMail
    'trashmail.com',
    'trashmail.de',

    // Other disposable-mail providers
    'maildrop.cc',
    'getnada.com',
    'getairmail.com',
    'dispostable.com',
    'discard.email',
    'fakeinbox.com',
    'mintemail.com',
];

// Reserved / obviously fake TLDs we don't want to accept
const FORBIDDEN_TLDS: string[] = ['test', 'example', 'invalid', 'localhost'];

// RFC 2606 reserved domains that should be rejected
const RESERVED_DOMAINS: string[] = [
    'example.com',
    'example.org',
    'example.net',
    'localhost.com',
    'localhost.localdomain',
];

// Keywords that indicate test/fake emails
const TEST_KEYWORDS: string[] = ['test', 'example', 'fake', 'dummy', 'sample'];

export interface EmailCheckResult {
    isEmpty: boolean;
    isFormatValid: boolean;
    suggestedDomain: string | null;
    isRoleAddress: boolean;
    isDisposable: boolean;
}

export interface EmailValidationOptions {
    minLength?: number;
    maxLength?: number;
}

const DEFAULT_MIN_EMAIL_LENGTH = 6;
const DEFAULT_MAX_EMAIL_LENGTH = 253;
const MAX_SUGGESTION_DISTANCE = 2;
const MAX_TLD_LENGTH = 24;

function levenshteinDistance(source: string, target: string): number {
    const sourceLength = source.length;
    const targetLength = target.length;

    if (sourceLength === 0) return targetLength;
    if (targetLength === 0) return sourceLength;

    // Space-optimized: only keep two rows at a time
    let previousRow: number[] = Array.from({ length: targetLength + 1 }, (_, i) => i);
    let currentRow: number[] = new Array<number>(targetLength + 1).fill(0);

    for (let i = 1; i <= sourceLength; i++) {
        currentRow[0] = i;

        for (let j = 1; j <= targetLength; j++) {
            const substitutionCost = source.charCodeAt(i - 1) === target.charCodeAt(j - 1) ? 0 : 1;

            const deletion = (previousRow[j] ?? 0) + 1;
            const insertion = (currentRow[j - 1] ?? 0) + 1;
            const substitution = (previousRow[j - 1] ?? 0) + substitutionCost;

            currentRow[j] = Math.min(deletion, insertion, substitution);
        }

        // Swap rows
        [previousRow, currentRow] = [currentRow, previousRow];
    }

    return previousRow[targetLength] ?? 0;
}

function hasForbiddenTld(domain: string): boolean {
    const parts = domain.toLowerCase().split('.');
    const tld = parts.at(-1);
    if (!tld) return false;
    return FORBIDDEN_TLDS.includes(tld);
}

function isTestDomain(domain: string): boolean {
    const normalizedDomain = domain.toLowerCase();

    // Check reserved domains
    if (RESERVED_DOMAINS.includes(normalizedDomain)) {
        return true;
    }

    // Split domain into parts by dots
    const domainParts = normalizedDomain.split(/[.-]/);

    // Check if any part exactly matches a test keyword
    return domainParts.some((part) => TEST_KEYWORDS.includes(part));
}

function isValidDomain(domain: string): boolean {
    // Domain must contain at least one dot
    if (!domain.includes('.')) return false;

    // Domain cannot start or end with a dot
    if (domain.startsWith('.') || domain.endsWith('.')) return false;

    // Split domain into parts (e.g., "gmail.com" → ["gmail", "com"])
    const parts = domain.split('.');

    // Each part must be non-empty
    if (parts.some((part) => !part)) return false;

    // The TLD (last part) must contain only letters and be at least 2 characters
    const tld = parts.at(-1);
    if (!tld || tld.length < 2 || tld.length > MAX_TLD_LENGTH) return false;
    if (!/^[a-zA-Z]+$/.test(tld)) return false;

    // Check if TLD looks suspicious (contains repeated characters or uncommon patterns)
    if (hasSuspiciousTld(tld)) return false;

    // Other parts can contain letters, numbers, and hyphens (but not start/end with hyphen)
    for (const part of parts.slice(0, -1)) {
        if (part.startsWith('-') || part.endsWith('-')) return false;
        if (!/^[a-zA-Z0-9-]+$/.test(part)) return false;
    }

    return true;
}

/**
 * Check if TLD looks suspicious
 * Catches common typos like "comv", "commm", "nett", etc.
 */
function hasSuspiciousTld(tld: string): boolean {
    const normalized = tld.toLowerCase();

    // Known valid common TLDs (whitelist approach for most common)
    const commonTlds = [
        'com',
        'net',
        'org',
        'edu',
        'gov',
        'mil',
        'int',
        'co',
        'uk',
        'us',
        'ca',
        'de',
        'fr',
        'jp',
        'cn',
        'au',
        'ru',
        'it',
        'nl',
        'se',
        'no',
        'es',
        'io',
        'ai',
        'me',
        'info',
        'biz',
        'name',
        'pro',
        'aero',
        'asia',
        'cat',
        'coop',
        'jobs',
        'mobi',
        'museum',
        'tel',
        'travel',
        'xxx',
    ];

    // If it's a common TLD, it's definitely valid
    if (commonTlds.includes(normalized)) {
        return false;
    }

    // Check for obvious typos of common TLDs using Levenshtein distance
    for (const commonTld of ['com', 'net', 'org', 'edu', 'gov']) {
        const distance = levenshteinDistance(normalized, commonTld);
        // If distance is 1, it's likely a typo (e.g., "comv" vs "com")
        if (distance === 1 && normalized.length > commonTld.length) {
            return true;
        }
    }

    // Additional pattern checks for suspicious TLDs
    // Check for repeated characters (e.g., "commm", "nett")
    if (/(.)\1{2,}/.test(normalized)) {
        return true;
    }

    return false;
}

function isBasicFormatValid(email: string, options: EmailValidationOptions = {}): boolean {
    const trimmed = email.trim();
    const minLength = options.minLength ?? DEFAULT_MIN_EMAIL_LENGTH;
    const maxLength = options.maxLength ?? DEFAULT_MAX_EMAIL_LENGTH;

    // Check length constraints
    if (trimmed.length < minLength || trimmed.length > maxLength) {
        return false;
    }

    // No spaces allowed
    if (trimmed.includes(' ')) {
        return false;
    }

    // Must have exactly one @
    const parts = trimmed.split('@');
    if (parts.length !== 2) {
        return false;
    }

    const [localPart, domainPart] = parts;

    // Local part and domain part must not be empty
    if (!localPart || !domainPart) {
        return false;
    }

    // Validate domain structure and TLD
    if (!isValidDomain(domainPart)) {
        return false;
    }

    // Check for forbidden TLDs
    if (hasForbiddenTld(domainPart)) {
        return false;
    }

    // Check for test/fake domains
    if (isTestDomain(domainPart)) {
        return false;
    }

    return true;
}

function findSuggestedDomain(domain: string, commonDomains: string[]): string | null {
    let bestMatch: string | null = null;
    let smallestDistance = Number.POSITIVE_INFINITY;

    for (const candidateDomain of commonDomains) {
        const distance = levenshteinDistance(domain, candidateDomain);

        if (distance < smallestDistance) {
            smallestDistance = distance;
            bestMatch = candidateDomain;
        }
    }

    if (!bestMatch) return null;

    if (smallestDistance === 0 || smallestDistance > MAX_SUGGESTION_DISTANCE) {
        return null;
    }

    return bestMatch;
}

function isRoleBasedLocalPart(localPart: string): boolean {
    const normalizedLocal = localPart.toLowerCase();
    return ROLE_EMAIL_PREFIXES.some((prefix) => normalizedLocal.startsWith(prefix));
}

function isDisposableDomain(domain: string): boolean {
    const normalizedDomain = domain.toLowerCase();
    return DISPOSABLE_DOMAINS.includes(normalizedDomain);
}

export function checkEmail(rawEmail: string, options: EmailValidationOptions = {}): EmailCheckResult {
    const email = rawEmail.trim();

    if (!email) {
        return {
            isEmpty: true,
            isFormatValid: false,
            suggestedDomain: null,
            isRoleAddress: false,
            isDisposable: false,
        };
    }

    const isFormatValid = isBasicFormatValid(email, options);

    let suggestedDomain: string | null = null;
    let isRoleAddress = false;
    let isDisposable = false;

    if (isFormatValid) {
        const atIndex = email.indexOf('@');
        const localPart = email.slice(0, atIndex);
        const domainPart = email.slice(atIndex + 1);

        const normalizedDomain = domainPart.toLowerCase();

        suggestedDomain = findSuggestedDomain(normalizedDomain, COMMON_EMAIL_DOMAINS);
        isRoleAddress = isRoleBasedLocalPart(localPart);
        isDisposable = isDisposableDomain(normalizedDomain);
    }

    return {
        isEmpty: false,
        isFormatValid,
        suggestedDomain,
        isRoleAddress,
        isDisposable,
    };
}
